import { useEffect, useState } from 'react';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  HOTKEY_SCOPES,
  KEYBOARD_BINDINGS,
  useActionHotkey,
  useHelpOverlayStore,
} from '@/hooks/hotkeys';

type BindingRow = { display: string; label: string };

const GROUPS: { heading: string; rows: readonly BindingRow[] }[] = [
  { heading: 'Go to', rows: KEYBOARD_BINDINGS.navigation },
  { heading: 'Proof of Exploit', rows: KEYBOARD_BINDINGS.poe },
  {
    heading: 'Technologies (with rows selected)',
    rows: KEYBOARD_BINDINGS.technology,
  },
  {
    heading: 'Vulnerabilities (with rows selected)',
    rows: KEYBOARD_BINDINGS.vulnerabilities,
  },
  {
    heading: 'Assets (with rows selected)',
    rows: KEYBOARD_BINDINGS.assets,
  },
  {
    heading: 'Seeds (with rows selected)',
    rows: KEYBOARD_BINDINGS.seeds,
  },
  {
    heading: 'Marcus (AI Assistant)',
    rows: KEYBOARD_BINDINGS.aiAssistant,
  },
  { heading: 'Help', rows: KEYBOARD_BINDINGS.help },
];

/**
 * KeyboardHelpOverlay — the `?` cheat sheet. App-shell singleton (mounted once
 * next to `HotkeyScopeSync`), NOT a reusable primitive, so it intentionally has
 * no Canonical-catalog row.
 *
 * Opened with `shift+slash` (react-hotkeys-hook matches `event.code`; `/` is
 * `Slash`, so the chord is `shift+slash` — do NOT use `'?'`, which matches the
 * produced char, not the code). `enableOnFormTags:false` means typing `?` in a
 * search box won't open it. The dialog is data-driven from `KEYBOARD_BINDINGS`
 * so it can never drift from the registered handlers. Radix `Dialog` supplies
 * Escape / overlay-click / focus-trap natively (no `useEscapeHotkey` needed).
 */
export const KeyboardHelpOverlay = () => {
  const [open, setOpen] = useState(false);
  const setHelpOpen = useHelpOverlayStore(s => s.setOpen);

  useActionHotkey('shift+slash', () => setOpen(o => !o), {
    // `overlay` scope (not the default `global`): HotkeyScopeSync disables
    // `global` whenever a drawer/modal is open, which would make `?` dead while
    // a drawer is open — contradicting the dialog's "Press a shortcut from
    // anywhere" copy. `overlay` is never globally suppressed, so `?` opens (and
    // toggles closed) the help even from behind a drawer. The help dialog is a
    // local-state Radix Dialog (it does not set modal.allModal.open), so it
    // doesn't suppress `overlay` itself; Radix Escape still closes it natively.
    scope: HOTKEY_SCOPES.overlay,
    enableOnFormTags: false,
  });

  // Publish open state to the shared store so HotkeyScopeSync (the SOLE writer of
  // the `global` scope) can suppress `global` action shortcuts while the cheat
  // sheet is open. This is a local-state Radix Dialog that does NOT set
  // modal.allModal.open, so HotkeyScopeSync can't otherwise see it. We must NOT
  // toggle the scope directly here: if we did, closing the help overlay would
  // unconditionally re-enable `global` even while a drawer/modal is still open
  // (the F1 regression). Mirroring into the store keeps `global` suppression a
  // single OR of all sources. Reset to false on unmount so a leftover `open`
  // flag can't strand `global` disabled.
  useEffect(() => {
    setHelpOpen(open);
    return () => setHelpOpen(false);
  }, [open, setHelpOpen]);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="z-[200] max-w-2xl">
        <DialogHeader>
          <DialogTitle>Keyboard shortcuts</DialogTitle>
          <DialogDescription>
            Press a shortcut from anywhere. Sequences like{' '}
            <span className="font-medium">g i</span> mean press g, then i.
          </DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
          {GROUPS.map(group => (
            <div key={group.heading} className="flex flex-col gap-2">
              <h3 className="text-chariot-text-secondary text-xs font-semibold tracking-wide uppercase">
                {group.heading}
              </h3>
              <ul className="flex flex-col gap-1.5">
                {group.rows.map(row => (
                  <li
                    key={row.display}
                    className="flex items-center justify-between gap-3"
                  >
                    <span className="text-chariot-text-primary text-sm">
                      {row.label}
                    </span>
                    <kbd className="bg-chariot-background-secondary text-chariot-text-primary border-chariot-stroke rounded border px-1.5 py-0.5 font-mono text-xs">
                      {row.display}
                    </kbd>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
};

KeyboardHelpOverlay.displayName = 'KeyboardHelpOverlay';
